"use client";

import type { ReactNode } from "react";
import type { CalendarFestivalDetail } from "@/lib/api/calendar";
import { FESTIVAL_FACTS } from "@/lib/festival-facts.generated";
import {
  TYPE_BADGE_CLASSES,
  TYPE_LABELS,
  TYPE_LABELS_HI,
  formatDate,
  festivalFaqs,
} from "../_data";

type BodyLocale = "en" | "hi";

// Section copy per locale. The body is BILINGUAL data either way (the backend
// returns name_en/name_hi + description_en/description_hi); `locale` only decides
// which language LEADS (H1 + first paragraph + section labels), so the Hindi twin
// reads Hindi-first and the English page is unchanged.
const COPY = {
  en: {
    fastingDay: "Fasting Day",
    hinduMonth: "Hindu month",
    about: "About This Festival",
    significance: "Significance",
    pujaVidhi: "Puja Vidhi (How to Observe)",
    regionalNames: "Regional Names",
  },
  hi: {
    fastingDay: "व्रत का दिन",
    hinduMonth: "हिंदू मास",
    about: "इस पर्व के बारे में",
    significance: "महत्व",
    pujaVidhi: "पूजा विधि (कैसे मनाएँ)",
    regionalNames: "क्षेत्रीय नाम",
  },
} as const;

function DateCard({ year, dateStr }: { year: string; dateStr: string }) {
  return (
    <div className="rounded-lg border border-border-interactive bg-surface-brand-subtle p-3.5">
      <div className="mb-1 text-caption font-semibold text-intent-brand">{year}</div>
      <div className="text-body-small font-semibold text-text-primary">{formatDate(dateStr)}</div>
    </div>
  );
}

export function FestivalBody({
  festival,
  locale = "en",
}: {
  festival: CalendarFestivalDetail;
  locale?: BodyLocale;
}) {
  return (
    <div data-design-owner="festival-body">
      <FestivalHeader festival={festival} locale={locale} />
      <FestivalDates festival={festival} />
      <FestivalPanchangBasis festival={festival} locale={locale} />
      <FestivalDescription festival={festival} locale={locale} />
      <FestivalSignificance festival={festival} locale={locale} />
      <FestivalPujaVidhi festival={festival} locale={locale} />
      <FestivalRegionalNames festival={festival} locale={locale} />
      <FestivalFAQ festival={festival} locale={locale} />
    </div>
  );
}

// Visible FAQ — the answer-first, machine-extractable surface for the "when is X /
// is X a vrat / what is the significance of X" queries this cluster is losing to
// AI Overviews (rules 48/49). Built from the SAME `festivalFaqs()` the FAQPage
// JSON-LD uses (parity by construction), so the schema question text is always
// visible on the page. Collapsed <details> keeps the read clean while the answer
// stays in the DOM for crawlers/AEO. Hidden below 2 Q&As so the schema and the
// visible render gate on the same threshold.
function FestivalFAQ({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  const faqs = festivalFaqs(festival, locale);
  if (faqs.length < 2) return null;
  const heading = locale === "hi" ? "अक्सर पूछे जाने वाले प्रश्न" : "Frequently Asked Questions";
  return (
    <div className="mb-6">
      <FestivalSectionTitle>{heading}</FestivalSectionTitle>
      <div className="flex flex-col gap-2">
        {faqs.map((f) => (
          <details key={f.question} className="border-b border-border-subtle pb-2">
            <summary className="cursor-pointer text-body-small font-semibold text-text-primary">
              {f.question}
            </summary>
            <p className="mt-2 text-body-small leading-relaxed text-text-secondary">{f.answer}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

function FestivalHeader({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  const badgeClasses = TYPE_BADGE_CLASSES[festival.type] || TYPE_BADGE_CLASSES.festival;
  const copy = COPY[locale];
  const typeLabels = locale === "hi" ? TYPE_LABELS_HI : TYPE_LABELS;
  // Hindi twin leads with the Hindi name as the H1 (message-match); English page
  // keeps the English name as H1. The other-language name is the subtitle.
  const heading = locale === "hi" ? festival.name_hi : festival.name_en;
  const subheading = locale === "hi" ? festival.name_en : festival.name_hi;

  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center gap-2.5">
        <span className={`rounded border px-2.5 py-0.5 text-caption font-semibold ${badgeClasses}`}>
          {typeLabels[festival.type] || festival.type}
        </span>
        {festival.fasting && (
          <span className="rounded bg-error/10 px-2.5 py-0.5 text-caption font-semibold text-error">
            {copy.fastingDay}
          </span>
        )}
      </div>

      <h1 className="mb-1 font-serif text-headline font-bold leading-tight text-text-primary">
        {heading}
      </h1>
      <p className="mb-2 text-subtitle text-text-muted">{subheading}</p>
      {festival.month_hi && (
        <p className="text-caption text-text-muted">
          {copy.hinduMonth}: <strong>{festival.month_hi}</strong>
        </p>
      )}
    </div>
  );
}

// Panchang basis — the engine-verified tithi/paksha/Hindu-month rule that fixes
// this festival's date each year, projected from festivals.yaml at build time
// (festival-facts.generated.ts, rules 23/24 — never hand-typed). This is the
// answer-first fact incumbent festival pages bury in prose: WHICH tithi defines
// the day, so readers understand why the Gregorian date moves every year.
type PanchangFacts = (typeof FESTIVAL_FACTS)[string];

function panchangBasisRows(facts: PanchangFacts, locale: BodyLocale): Array<[string, string]> {
  if (facts.rule_type === "sankranti") {
    return [
      [
        locale === "hi" ? "प्रकार" : "Basis",
        locale === "hi" ? "सूर्य संक्रांति (सौर पर्व)" : "Surya Sankranti (solar transition)",
      ],
    ];
  }
  const paksha = locale === "hi" && facts.paksha_hi ? facts.paksha_hi : facts.paksha;
  const month = facts.hindu_month_hi
    ? `${facts.hindu_month} (${facts.hindu_month_hi})`
    : facts.hindu_month;
  return [
    [locale === "hi" ? "तिथि" : "Tithi", facts.tithi],
    [locale === "hi" ? "पक्ष" : "Paksha", paksha],
    [locale === "hi" ? "हिंदू मास" : "Hindu month", month],
  ];
}

function panchangBasisExplainer(facts: PanchangFacts, locale: BodyLocale): string {
  if (facts.rule_type === "sankranti") {
    return locale === "hi"
      ? "यह सौर पर्व है — इसकी तिथि सूर्य के राशि-प्रवेश (संक्रांति) से तय होती है, इसलिए यह लगभग हर वर्ष एक ही ग्रेगोरियन तिथि के आस-पास आता है।"
      : "This is a solar observance — its day is fixed by Surya's entry into a new rashi (Sankranti), which is why it falls on nearly the same Gregorian date every year, unlike tithi-based festivals.";
  }
  if (locale === "hi") {
    const month = facts.hindu_month_hi || facts.hindu_month;
    const paksha = facts.paksha_hi || facts.paksha;
    return `हर वर्ष यह पर्व ${month} मास के ${paksha} की ${facts.tithi} तिथि को मनाया जाता है। चंद्र तिथि और ग्रेगोरियन कैलेंडर के अंतर के कारण अंग्रेज़ी तारीख़ हर वर्ष बदलती है।`;
  }
  return `Each year this observance falls on the ${facts.tithi} tithi of the ${facts.paksha} Paksha in the Hindu month of ${facts.hindu_month}. Because lunar tithis drift against the Gregorian calendar, the English date shifts every year — the tithi rule above is what stays fixed.`;
}

function FestivalPanchangBasis({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  const facts = FESTIVAL_FACTS[festival.id];
  if (!facts || (!facts.tithi && facts.rule_type !== "sankranti")) return null;

  const heading = locale === "hi" ? "पंचांग आधार (तिथि नियम)" : "Panchang Basis (Tithi Rule)";
  const rows = panchangBasisRows(facts, locale);
  const explainer = panchangBasisExplainer(facts, locale);

  return (
    <div className="mb-6">
      <FestivalSectionTitle>{heading}</FestivalSectionTitle>
      <div className="mb-2 flex flex-wrap gap-3">
        {rows.map(([label, value]) =>
          value ? (
            <div
              key={label}
              className="rounded-lg border border-border-interactive bg-surface-brand-subtle px-3.5 py-2.5"
            >
              <div className="mb-0.5 text-caption font-semibold text-intent-brand">{label}</div>
              <div className="text-body font-semibold text-text-primary">{value}</div>
            </div>
          ) : null
        )}
      </div>
      <p className="text-body leading-relaxed text-text-secondary">{explainer}</p>
    </div>
  );
}

function FestivalDates({ festival }: { festival: CalendarFestivalDetail }) {
  return (
    <div className="mb-6 grid grid-cols-2 gap-3">
      {festival.date_2026 && <DateCard year="2026" dateStr={festival.date_2026} />}
      {festival.date_2027 && <DateCard year="2027" dateStr={festival.date_2027} />}
    </div>
  );
}

function FestivalDescription({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  // Lead with the locale's language; show the other as the secondary paragraph
  // (the backend returns both; only the lead order changes per locale).
  const lead = locale === "hi" ? festival.description_hi : festival.description_en;
  const secondary = locale === "hi" ? festival.description_en : festival.description_hi;
  return (
    <div className="mb-6">
      <FestivalSectionTitle>{COPY[locale].about}</FestivalSectionTitle>
      <p className="mb-2 text-body-small leading-relaxed text-text-secondary">{lead}</p>
      {secondary && (
        <p className="text-body-small leading-relaxed text-text-secondary">{secondary}</p>
      )}
    </div>
  );
}

function FestivalSignificance({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  if (!festival.significance) return null;

  return (
    <div className="mb-6">
      <FestivalSectionTitle>{COPY[locale].significance}</FestivalSectionTitle>
      <p className="text-body-small leading-relaxed text-text-secondary">{festival.significance}</p>
    </div>
  );
}

function FestivalPujaVidhi({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  if (!festival.puja_vidhi) return null;

  return (
    <div className="mb-6 rounded-lg border border-success/20 bg-success/5 p-5">
      <FestivalSectionTitle tone="success">{COPY[locale].pujaVidhi}</FestivalSectionTitle>
      <p className="text-body-small leading-relaxed text-text-secondary">{festival.puja_vidhi}</p>
    </div>
  );
}

function FestivalRegionalNames({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  if (festival.regional_names.length === 0) return null;

  return (
    <div className="mb-6">
      <FestivalSectionTitle>{COPY[locale].regionalNames}</FestivalSectionTitle>
      <div className="flex flex-wrap gap-2">
        {festival.regional_names.map((name) => (
          <span
            key={name}
            className="rounded border border-border-subtle bg-surface-raised px-3 py-1 text-caption text-text-primary"
          >
            {name}
          </span>
        ))}
      </div>
    </div>
  );
}

// FestivalProducts removed — physical storefront retired (digital-only 2026-06)

function FestivalSectionTitle({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: "default" | "success";
}) {
  const toneClass = tone === "success" ? "text-success" : "text-text-primary";
  return <h2 className={`mb-2 font-serif text-subtitle font-bold ${toneClass}`}>{children}</h2>;
}
